import { HStack, Spacer, VStack } from '@chakra-ui/react';
import classNames from 'classnames';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import {
  CursorTooltip,
  getCursorPosition,
  PlotTooltip,
  type TooltipPosition,
  useTooltipController,
} from '../plot-tooltip';
import type { buildSegmentEntries } from './stacked-bar-shared';
import styles from './stacked-bar-tooltip.module.css';

type StackedBarTooltipProps = {
  children: ReactElement;
  title: string;
  titleRight?: string;
  entries?: {
    label: string;
    active: boolean;
    mainValue: string;
    secondaryValue?: string;
    /** Indented under the entry above it, as a part of that entry. */
    nested?: boolean;
  }[];
};

const StackedBarTooltipContent = ({
  title,
  titleRight,
  entries,
}: Omit<StackedBarTooltipProps, 'children'>) => (
  <VStack
    spacing={0}
    align="stretch"
    px="7px"
    className={styles.barTooltipBody}
  >
    <HStack justify="space-between" align="baseline" spacing={4}>
      <div className={styles.barTooltipLabel}>{title}</div>
      {titleRight && (
        <div className={styles.barTooltipTitleRight}>{titleRight}</div>
      )}
    </HStack>
    {entries === undefined && (
      <div className={styles.barTooltipValueNoData}>No Data</div>
    )}
    {entries?.map((entry, index) => {
      return (
        <HStack
          key={index}
          spacing={4}
          height="19px"
          className={classNames(styles.barTooltipRow, {
            [styles.nestedRow as string]: entry.nested,
          })}
        >
          <div
            className={classNames(styles.barTooltipValue, {
              [styles.highlight as string]: entry.active,
              [styles.nestedLabel as string]: entry.nested,
            })}
          >
            {entry.label}
          </div>
          <Spacer />
          <div
            className={classNames(styles.barTooltipValue, {
              [styles.highlight as string]: entry.active,
            })}
          >
            {entry.secondaryValue
              ? `${entry.mainValue} / ${entry.secondaryValue}`
              : entry.mainValue}
          </div>
        </HStack>
      );
    })}
  </VStack>
);

export const StackedBarTooltip = ({
  title,
  titleRight,
  entries,
  children,
}: StackedBarTooltipProps) => {
  return (
    <CursorTooltip
      label={
        <StackedBarTooltipContent
          title={title}
          titleRight={titleRight}
          entries={entries}
        />
      }
    >
      {children}
    </CursorTooltip>
  );
};

export type StackedBarTooltipState = {
  key: string;
  title: string;
  /** Bar (row/column) index the hover resolved to. */
  row: number;
  entries: ReturnType<typeof buildSegmentEntries> | undefined;
};

export const useStackedBarTooltip = (
  hitTest: (pointer: [number, number]) => StackedBarTooltipState | null
) => {
  const [state, setState] = useState<StackedBarTooltipState | null>(null);
  const [cursor, setCursor] = useState<TooltipPosition | null>(null);
  const keyRef = useRef<string | null>(null);

  const { isVisible, gProps } = useTooltipController({
    onHover: (pointer, event) => {
      setCursor(getCursorPosition(event));
      const next = hitTest(pointer);
      if (next === null || keyRef.current === next.key) return;
      keyRef.current = next.key;
      setState(next);
    },
  });

  useEffect(() => {
    if (!isVisible) {
      keyRef.current = null;
      setState(null);
      setCursor(null);
    }
  }, [isVisible]);

  return { state, cursor, isVisible, gProps };
};

export const StackedBarSharedTooltip = ({
  state,
  cursor,
  isVisible,
}: {
  state: StackedBarTooltipState | null;
  cursor: TooltipPosition | null;
  isVisible: boolean;
}) => {
  // v1.1 places the shared tooltip above/below the cursor.
  if (!state || !cursor) return null;
  return (
    <PlotTooltip
      isVisible={isVisible}
      x={cursor.x}
      y={cursor.y}
      placement="vertical"
      space="viewport"
    >
      <StackedBarTooltipContent title={state.title} entries={state.entries} />
    </PlotTooltip>
  );
};
